import {
  CarrierLogo,
  Column,
  FormCheckBox,
  ModalBase,
  Row,
  Typography,
} from '@/components'
import { WithInfoText } from '@/components/hoc/WithInfoText'
import { ComponentColors, ThemeColor } from '@/enums'
import { useSegment } from '@/modules'
import { useFormContext } from '@/providers'
import { usePreviewFile } from '@/services'
import { useLocationTypes } from '@/services/locationTypes'
import { theme } from '@/theme'
import { useModal } from '@/utils'
import { compactMoney, ddMMyyyy } from '@/utils/convert'
import { useCategoryTranslations } from '@/utils/hooks/useCategoryTranslations'
import { Button, Skeleton } from '@surein/ui'
import {
  Ambulance,
  Megaphone,
  MessageSquareHeart,
  PaintBucket,
  PlayCircle,
  ShieldCheck,
  Stethoscope,
  Trophy,
  LucideIcon,
} from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useOutletContext, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { UnderlinedButtonLink } from '../styles'
import { useValidateCheckoutStep } from './helpers'
import { CartRecommendationProduct } from '@/types/recommendation'

const categoryIcons: Record<string, LucideIcon[]> = {
  LI: [Ambulance, Stethoscope, PaintBucket, Megaphone],
}

const StyledCarrierLogo = styled(CarrierLogo)`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.glass200};
`

const RecommendedBadge = styled.div`
  background-color: ${({ theme }) => theme.color.glass100};
  padding: 4px 8px;
  border-radius: 100px;
  flex-shrink: 0;
  width: fit-content;
`

const ProtectionEntry = styled(Row)`
  gap: 8px;
  align-items: center;
  > svg {
    flex-shrink: 0;
  }
`

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;

  & > div:last-child {
    grid-column: span 2;
  }
`

const CheckboxContainer = styled(Row)`
  & > div > div {
    align-items: center;
  }
`

const InformationContainer = styled(Column)`
  gap: 16px;
  padding: 0 8px;

  > span:first-child {
    padding-right: 32px;
  }
`

const WhatIsProtectedCard = styled(Column)`
  background-color: ${({ theme }) => theme.color.glass100};
  padding: 16px;
  border-radius: 16px;
  gap: 16px;
`

const InfoDescription = styled(Typography).attrs({
  variant: 'description',
})`
  font-weight: 500;
`

interface ProtectionInformationProps {
  categoryUsps: string[]
  categoryId: string
  onButtonClick?: () => void
  buttonText?: string
  iconColor?: string
}

const ProtectionInformation = ({
  categoryUsps,
  categoryId,
  onButtonClick,
  buttonText,
  iconColor,
}: ProtectionInformationProps) => {
  const { t } = useTranslation()

  return (
    <WhatIsProtectedCard>
      <Row justifyContent="space-between">
        <Typography variant="subheadline">
          {t('checkout.overview.whatIsProtected')}
        </Typography>
        {onButtonClick && buttonText && (
          <UnderlinedButtonLink
            onClick={onButtonClick}
            size="small"
            color={ComponentColors.primary}
          >
            {buttonText}
          </UnderlinedButtonLink>
        )}
      </Row>
      <Column gap="8px">
        {categoryUsps.map((usp, index) => {
          const Icon = categoryIcons[categoryId]?.[index] || ShieldCheck
          return usp ? (
            <ProtectionEntry key={usp}>
              <Icon color={iconColor} />
              <Typography variant="description">{usp}</Typography>
            </ProtectionEntry>
          ) : null
        })}
      </Column>
    </WhatIsProtectedCard>
  )
}

interface InformationModalProps {
  isOpen: boolean
  onClose: () => void
  categoryId: string
  offerTag: string
  downloadAllFiles: () => void
  shouldShowDownloadAllFiles: boolean
}

const InformationModal = ({
  isOpen,
  onClose,
  categoryId,
  offerTag,
  downloadAllFiles,
  shouldShowDownloadAllFiles,
}: InformationModalProps) => {
  const { t } = useTranslation()
  const categoryTranslations = useCategoryTranslations(categoryId)
  const categoryUsps = categoryTranslations.usps

  return (
    <ModalBase closable isOpen={isOpen} onClose={onClose} padding="16px">
      <InformationContainer>
        <Typography bold variant="subheadline">
          {categoryTranslations.categoryName}
        </Typography>
        <RecommendedBadge>
          <Typography variant="footnote" color={ThemeColor.success1}>
            {t(`${offerTag}`)}
          </Typography>
        </RecommendedBadge>
        <Column gap="16px">
          <Row gap="8px">
            <MessageSquareHeart />
            <Typography variant="description">
              {t('checkout.overview.cancelAnytime')}
            </Typography>
          </Row>
          <Row gap="8px">
            <PlayCircle />
            <Typography variant="description">
              {t('checkout.overview.insuranceCertificatePdf')}
            </Typography>
          </Row>
          <Row gap="8px">
            <Trophy />
            <Typography variant="description">
              {t('checkout.overview.topPerformance')}
            </Typography>
          </Row>
        </Column>
        <ProtectionInformation
          categoryUsps={categoryUsps}
          categoryId={categoryId}
          iconColor={theme.color.info1}
        />
        <Typography variant="footnote" color="glass500">
          {t('checkout.overview.insuranceOverviewNote')}
        </Typography>
        {shouldShowDownloadAllFiles && (
          <Button variant="primary" shape="square" onClick={downloadAllFiles}>
            {t('checkout.overview.readAllConditions')}
          </Button>
        )}
      </InformationContainer>
    </ModalBase>
  )
}

interface CheckoutOverviewStepProps {
  setButtonState: (state: { onClick?: () => void; loading?: boolean }) => void
  onNext: () => void
}

interface CartContext {
  cart: Record<string, CartRecommendationProduct[]>
}

export const CheckoutOverviewStep = ({
  setButtonState,
  onNext,
}: CheckoutOverviewStepProps) => {
  const { t } = useTranslation()
  const { locationId } = useParams<{ locationId: string }>()
  const { cart } = useOutletContext<CartContext>()
  const { values } = useFormContext()
  const data = locationId ? cart[locationId] : []
  const analytics = useSegment()
  const previewFile = usePreviewFile()
  const {
    data: locationTypesData,
    isLoading: locationTypesLoading,
  } = useLocationTypes()

  // This page is only shown when there is only one product in the cart
  const firstProduct = data?.[0]
  const categoryTranslations = useCategoryTranslations(
    firstProduct?.categoryId || '',
  )
  const categoryUsps = categoryTranslations.usps

  const {
    open: openInformationModal,
    close: closeInformationModal,
    isOpen: isInformationModalOpen,
  } = useModal()

  const handleSuccess = () => {
    analytics.track('app_checkout_overview')
    onNext()
  }

  const handleNext = useValidateCheckoutStep(handleSuccess)

  useEffect(() => {
    setButtonState({ onClick: handleNext })

    return () => {
      setButtonState({ loading: false })
    }
  }, [values])

  const handlePreview = ({ name }: { name: string }) => {
    previewFile.mutate(name)
  }

  const downloadAllFiles = () => {
    const files = firstProduct?.recommendationProductFiles || []

    files.forEach((file) => {
      handlePreview({
        name: file?.name || '',
      })
    })
  }

  if (!firstProduct) {
    return null
  }

  return (
    <Column gap="16px">
      <Row alignItems="center" justifyContent="space-between" gap="16px">
        <Row alignItems="center" gap="8px">
          {firstProduct.carrierData.logoUrl ? (
            <StyledCarrierLogo name={firstProduct.carrierData.logoUrl} />
          ) : null}
          <Typography ellipsis variant="p2Body">
            {firstProduct.name}
          </Typography>
        </Row>
        <RecommendedBadge>
          <Typography
            noWordBreak
            variant="footnote"
            color={ThemeColor.success1}
          >
            {t(`${firstProduct.offerTag}`)}
          </Typography>
        </RecommendedBadge>
      </Row>
      <ProtectionInformation
        categoryUsps={categoryUsps}
        categoryId={firstProduct.categoryId}
        onButtonClick={openInformationModal}
        buttonText={t('checkout.overview.details')}
      />
      <GridContainer>
        <Column gap="4px">
          <WithInfoText
            variant="secondary"
            content={t('checkout.overview.insuredSumInfo')}
          >
            <Typography variant="footnote" color="glass500">
              {t('checkout.overview.insuredSum')}
            </Typography>
          </WithInfoText>
          <InfoDescription>
            {firstProduct.amountInsuredUnlimited
              ? t('unlimited')
              : firstProduct.amountInsured
              ? compactMoney(firstProduct.amountInsured)
              : ''}
          </InfoDescription>
        </Column>
        <Column gap="4px">
          <WithInfoText
            variant="secondary"
            content={t('checkout.overview.deductibleInfo')}
          >
            <Typography variant="footnote" color="glass500">
              {t('checkout.overview.deductible')}
            </Typography>
          </WithInfoText>
          <InfoDescription>
            {compactMoney(firstProduct.deductible || 0)}
          </InfoDescription>
        </Column>
        <Column gap="4px">
          <WithInfoText
            variant="secondary"
            content={t('checkout.overview.startDateInfo')}
          >
            <Typography variant="footnote" color="glass500">
              {t('checkout.overview.startDate')}
            </Typography>
          </WithInfoText>
          <InfoDescription>
            {firstProduct.startDate ? ddMMyyyy(firstProduct.startDate) : ''}
          </InfoDescription>
        </Column>
        <Column gap="4px">
          <WithInfoText
            variant="secondary"
            content={t('checkout.overview.paymentPeriodInfo')}
          >
            <Typography variant="footnote" color="glass500">
              {t('checkout.overview.paymentPeriod')}
            </Typography>
          </WithInfoText>
          <InfoDescription>
            {t(`paymentPeriodOptions.${firstProduct.paymentPeriod}`)}
          </InfoDescription>
        </Column>
        <Column gap="4px">
          <WithInfoText
            variant="secondary"
            content={t('checkout.overview.coverageInfo')}
          >
            <Typography variant="footnote" color="glass500">
              {t('checkout.overview.coverage')}
            </Typography>
          </WithInfoText>
          {locationTypesLoading ? (
            <Skeleton width="240px" />
          ) : (
            <InfoDescription>
              {locationTypesData?.getLocationLabel(firstProduct.risksInsured) ||
                firstProduct.risksInsured}
            </InfoDescription>
          )}
        </Column>
      </GridContainer>
      <WithInfoText
        variant="secondary"
        content={t('checkout.overview.insuranceTermsConfirmation')}
      >
        <CheckboxContainer>
          <FormCheckBox
            id="insuranceTerms"
            label={t('checkout.overview.insuranceTermsConfirmation')}
            name="insuranceTerms"
            noMargin
          />
        </CheckboxContainer>
      </WithInfoText>
      <InformationModal
        isOpen={isInformationModalOpen}
        onClose={closeInformationModal}
        categoryId={firstProduct.categoryId}
        offerTag={firstProduct.offerTag || ''}
        shouldShowDownloadAllFiles={
          (firstProduct.recommendationProductFiles?.length || 0) > 0
        }
        downloadAllFiles={downloadAllFiles}
      />
    </Column>
  )
}
